import { isBrowser } from '@lib/environment/isBrowser';

const ANONYMOUS_ID_STORAGE_KEY = 'fb_anonymous_id';

/*
 * Returns a stable, randomly-generated identifier for the current browser,
 * persisted in localStorage. Used to give logged-out visitors a synthetic
 * Sentry user id so anonymous events can be counted as unique users instead of
 * collapsing into a single unattributed bucket.
 *
 * Contains no PII — it is an opaque UUID, not derived from the user. Returns
 * undefined on the server or when localStorage is unavailable (e.g. private
 * mode), in which case the caller should leave the Sentry user unset.
 */
export function getOrCreateAnonymousId(): string | undefined {
  if (!isBrowser()) {
    return undefined;
  }

  try {
    // We're not useing localStorageWrapper as it contains a path reporting
    // to Sentry, creating a circular dependency.
    // Typed wider than the DOM lib: Android WebViews with DOM storage disabled
    // expose null, and some macOS Safari/Apple Mail contexts expose undefined.
    const storage = window.localStorage as Storage | null | undefined;
    if (!storage) {
      return undefined;
    }

    const existing = storage.getItem(ANONYMOUS_ID_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const id = crypto.randomUUID();
    storage.setItem(ANONYMOUS_ID_STORAGE_KEY, id);
    return id;
  } catch (error) {
    // localStorage access throws a DOMException when storage is unavailable —
    // SecurityError when cookies/storage are blocked (e.g. Safari private mode)
    // or QuotaExceededError when full. In that case we skip anonymous
    // attribution rather than break error reporting. Anything else is
    // unexpected, so rethrow and let it surface.
    if (error instanceof DOMException) {
      return undefined;
    }
    throw error;
  }
}
